import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { Params, RouterLink } from '@angular/router';
import { Observable, Subject, catchError, forkJoin, map, of, switchMap, takeUntil, timeout } from 'rxjs';
import { CheckCircle2, ChevronRight, CircleAlert, CircleDollarSign, Database, FileText, Landmark, Percent, Plus, RefreshCw, Scale, ShieldCheck, TrendingDown, TrendingUp, TriangleAlert, WalletCards, LucideAngularModule } from 'lucide-angular';

import { FinancialDeliveryOccurrence } from '@core/models/bank-schedule.model';
import { BankScheduleApiService } from '@core/services/bank-schedule-api.service';
import { MeService } from '@core/services/me.service';
import { TenantService } from '@core/services/tenant.service';
import { FinancialActivityPeriod, FinancialPeriodReadiness, FinancialProfile, FinancialSource, FinancialSourceActivity } from '@features/reconciliation/models/financial-reconciliation.model';
import { SourceAgnosticApiService } from '@features/reconciliation/source-agnostic/source-agnostic-api.service';
import { FinancialReconciliationService } from '@features/reconciliation/services/financial-reconciliation.service';
import { ReconciliationService } from '@features/reconciliation/services/reconciliation.service';
import { IntegrationJobHealth, IntegrationRunsApiService } from '@features/reconciliation/services/integration-runs-api.service';
import { EMPTY_FISCAL_DASHBOARD_STATE, FiscalDashboardState, loadFiscalDashboardState } from '@features/reconciliation/utils/fiscal-dashboard-state';
import { DEFAULT_PERIOD, formatPeriodForApi, getPeriodLabelForApiPeriod } from '@features/reconciliation/utils/period.utilities';
import { AppButtonComponent } from '@shared/components/ui/button';
import { BrandLoaderComponent } from '@shared/components/brand-loader/brand-loader.component';
import { PeriodFilterApply, PeriodFilterComponent, PeriodFilterMode } from '@shared/components/ui/period-filter';

const FEED_TYPES = ['BankMovement', 'BankStatement', 'AcquiringTransaction', 'AcquiringSettlement', 'AggregatorTransaction', 'AggregatorSettlement', 'PosTransaction', 'PosClosure', 'ElectronicInvoice'];
const BANK_FEED_TYPES = ['BankMovement', 'BankStatement'];
const ACQUIRING_FEED_TYPES = ['AcquiringTransaction', 'AcquiringSettlement'];
const AGGREGATOR_FEED_TYPES = ['AggregatorTransaction', 'AggregatorSettlement'];
const POS_FEED_TYPES = ['PosTransaction', 'PosClosure'];

interface DashboardAlert { tone: 'critical' | 'warning' | 'info' | 'success'; title: string; description: string; action: string; href: string; queryParams?: Params; }
interface ReconciliationStatusSummary { reconciled: number; differences: number; pending: number; total: number; }
interface SourceAgnosticDashboardState { status: ReconciliationStatusSummary; failedExecutions: number; canceledExecutions: number; routeCount?: number; }

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterLink, LucideAngularModule, AppButtonComponent, PeriodFilterComponent, BrandLoaderComponent],
  host: { class: 'block w-full min-h-full' },
  templateUrl: './home.component.html',
  styleUrl: './home.component.css'
})
export class HomeComponent implements OnInit, OnDestroy {
  private readonly reconciliation = inject(FinancialReconciliationService);
  private readonly sourceAgnostic = inject(SourceAgnosticApiService);
  private readonly fiscalApi = inject(ReconciliationService);
  private readonly integrationsApi = inject(IntegrationRunsApiService);
  private readonly schedules = inject(BankScheduleApiService);
  private readonly tenantService = inject(TenantService);
  private readonly meService = inject(MeService);
  private readonly destroy$ = new Subject<void>();

  readonly CheckCircle2 = CheckCircle2; readonly ChevronRight = ChevronRight; readonly CircleAlert = CircleAlert;
  readonly CircleDollarSign = CircleDollarSign; readonly Database = Database; readonly FileText = FileText; readonly Landmark = Landmark; readonly Percent = Percent; readonly Plus = Plus; readonly RefreshCw = RefreshCw;
  readonly Scale = Scale; readonly ShieldCheck = ShieldCheck;
  readonly TrendingDown = TrendingDown; readonly TrendingUp = TrendingUp;
  readonly TriangleAlert = TriangleAlert; readonly WalletCards = WalletCards;
  readonly emptyStateIcon = '/assets/reconciliation/icon-no-data.svg';

  readonly loading = signal(true); readonly refreshing = signal(false); readonly error = signal<string | null>(null);
  readonly partialData = signal(false);
  readonly periods = signal<FinancialActivityPeriod[]>([]);
  readonly selectedPeriod = signal(formatPeriodForApi(DEFAULT_PERIOD));
  readonly periodMode = signal<PeriodFilterMode>('month');
  readonly periodFromDate = signal<string | null>(null);
  readonly periodToDate = signal<string | null>(null);
  readonly activity = signal<FinancialSourceActivity | null>(null);
  readonly acquiringActivity = signal<FinancialSourceActivity | null>(null);
  readonly aggregatorActivity = signal<FinancialSourceActivity | null>(null);
  readonly posActivity = signal<FinancialSourceActivity | null>(null);
  readonly readiness = signal<FinancialPeriodReadiness | null>(null);
  readonly sources = signal<FinancialSource[]>([]); readonly profiles = signal<FinancialProfile[]>([]);
  readonly reconciliationStatus = signal<ReconciliationStatusSummary>({ reconciled: 0, differences: 0, pending: 0, total: 0 });
  readonly failedReconciliationExecutions = signal(0);
  /** Recorridos del dinero publicados (Multifuente) en el período. */
  readonly routeCount = signal(0);
  /** Resultado del modo Fiscal en el periodo (una sola verdad: el Inicio también lo cuenta). */
  readonly fiscalState = signal<FiscalDashboardState>(EMPTY_FISCAL_DASHBOARD_STATE);
  /** Integraciones (Edge) cuya última corrida falló: se avisa el mismo día. */
  readonly failedIntegrations = signal<IntegrationJobHealth[]>([]);
  /** Pantalla de conciliación que corresponde al modo que usa el tenant. */
  readonly reconciliationHref = computed(() => !this.routeCount() && this.fiscalState().hasRuns
    ? '/reconciliation/fiscal' : '/reconciliation/source-agnostic/plans');
  readonly differencesHref = computed(() => !this.routeCount() && this.fiscalState().hasRuns
    ? '/reconciliation/fiscal' : '/reconciliation/review');
  readonly canceledReconciliationExecutions = signal(0);
  readonly deliveries = signal<FinancialDeliveryOccurrence[]>([]);
  readonly updatedAt = signal<Date | null>(null);
  readonly activeFlowIndex = signal<number | null>(null);

  readonly tenantName = computed(() => {
    const key = this.tenantService.tenantKey();
    return this.meService.me()?.tenants.find(item => item.tenantKey === key)?.displayName ?? key ?? 'Tu operación';
  });
  readonly userName = computed(() => {
    const displayName = this.meService.me()?.displayName?.trim();
    return displayName?.split(/\s+/)[0] || 'Hola';
  });
  readonly hasData = computed(() => (this.activity()?.totalItems ?? 0) > 0 || (this.acquiringActivity()?.totalItems ?? 0) > 0);
  readonly netFlow = computed(() => (this.activity()?.totalCredits ?? 0) - (this.activity()?.totalDebits ?? 0));
  readonly acquiringNetFlow = computed(() => (this.acquiringActivity()?.totalCredits ?? 0) - (this.acquiringActivity()?.totalDebits ?? 0));
  readonly aggregatorNetFlow = computed(() => (this.aggregatorActivity()?.totalCredits ?? 0) - (this.aggregatorActivity()?.totalDebits ?? 0));
  readonly posNetFlow = computed(() => (this.posActivity()?.totalCredits ?? 0) - (this.posActivity()?.totalDebits ?? 0));
  readonly financialMargin = computed(() => {
    const income = this.activity()?.totalCredits ?? 0;
    return income ? Math.round((this.netFlow() / income) * 1000) / 10 : 0;
  });
  readonly expenseCoverage = computed(() => {
    const expenses = this.activity()?.totalDebits ?? 0;
    return expenses ? Math.round(((this.activity()?.totalCredits ?? 0) / expenses) * 1000) / 10 : 0;
  });
  readonly averageMovement = computed(() => {
    const items = this.activity()?.totalItems ?? 0;
    return items ? ((this.activity()?.totalCredits ?? 0) + (this.activity()?.totalDebits ?? 0)) / items : 0;
  });
  readonly maxBankFlowAmount = computed(() => Math.max(
    1,
    this.activity()?.totalCredits ?? 0,
    this.activity()?.totalDebits ?? 0,
    Math.abs(this.netFlow())
  ));
  readonly maxAcquiringFlowAmount = computed(() => Math.max(
    1,
    this.acquiringActivity()?.totalCredits ?? 0,
    this.acquiringActivity()?.totalDebits ?? 0,
    Math.abs(this.acquiringNetFlow())
  ));
  readonly reconciledCount = computed(() => this.reconciliationStatus().reconciled);
  readonly exceptionCount = computed(() => this.reconciliationStatus().differences);
  readonly totalReconciliationMovements = computed(() => this.reconciliationStatus().total);
  readonly pendingReconciliationCount = computed(() => this.reconciliationStatus().pending);
  /** Porcentaje conciliado como número (para estilos y comparaciones). */
  readonly reconciliationRateValue = computed(() => {
    const total = this.totalReconciliationMovements();
    return total ? Math.round((this.reconciledCount() / total) * 1000) / 10 : 0;
  });
  /** Porcentaje conciliado para mostrar (formato colombiano). */
  readonly reconciliationRate = computed(() => new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(this.reconciliationRateValue()));
  readonly readySourceCount = computed(() => (this.readiness()?.sources ?? []).filter(source => source.isReady).length);
  readonly evaluatedSourceCount = computed(() => this.readiness()?.sources.length ?? 0);
  readonly readyRate = computed(() => this.evaluatedSourceCount()
    ? Math.round((this.readySourceCount() / this.evaluatedSourceCount()) * 100) : 0);
  readonly deliveryStats = computed(() => ({
    total: this.deliveries().length,
    received: this.deliveries().filter(item => item.status === 'Received').length,
    pending: this.deliveries().filter(item => item.status === 'Pending').length,
    attention: this.deliveries().filter(item => item.status === 'Overdue' || item.status === 'ReceivedWithErrors').length
  }));
  readonly synchronization = computed(() => (this.readiness()?.sources ?? [])
    .slice()
    .sort((a, b) => Number(b.isReady) - Number(a.isReady) || a.sourceName.localeCompare(b.sourceName))
    .slice(0, 5));
  readonly activeFlowDay = computed(() => {
    const days = this.activity()?.dailyBreakdown ?? [];
    const index = this.activeFlowIndex();
    return index === null ? null : days[index] ?? null;
  });


  readonly alerts = computed<DashboardAlert[]>(() => {
    const result: DashboardAlert[] = [];
    const pendingFileDeliveries = this.deliveries().filter(item =>
      item.originMode !== 'ExternalQuery' && (item.status === 'Pending' || item.status === 'Overdue'));
    const missingSources = [...new Map(pendingFileDeliveries.map(item => [item.financialSourceId, item])).values()];
    const deliveryProblems = this.deliveries().filter(item =>
      item.status === 'ReceivedWithErrors' || (item.originMode === 'ExternalQuery' && item.status === 'Overdue'));
    if (this.partialData()) result.push({ tone: 'info', title: 'Lectura parcial del periodo', description: 'Algunos indicadores no respondieron. La información disponible se mantiene visible.', action: 'Revisar operación', href: '/paybridge/schedules' });
    const failedIntegrations = this.failedIntegrations();
    if (failedIntegrations.length) result.push({ tone: 'critical', title: `${failedIntegrations.length} ${failedIntegrations.length === 1 ? 'integración con falla' : 'integraciones con falla'}`, description: `${failedIntegrations[0].jobName}: ${failedIntegrations[0].lastRun.errorMessage || 'la última carga automática no terminó.'}`, action: 'Ver integraciones', href: '/reconciliation/configuracion/integraciones' });
    if (deliveryProblems.length) result.push({ tone: 'critical', title: `${deliveryProblems.length} entregas requieren atención`, description: 'Hay entregas con errores o conectores vencidos que pueden afectar el cierre.', action: 'Revisar seguimiento', href: '/paybridge/schedules', queryParams: { period: this.selectedPeriod() } });
    if (missingSources.length) {
      const first = missingSources[0];
      result.push({
        tone: 'warning',
        title: `${missingSources.length} ${missingSources.length === 1 ? 'fuente programada pendiente' : 'fuentes programadas pendientes'} de carga`,
        description: missingSources.slice(0, 2).map(item => item.financialSourceName).join(' · '),
        action: 'Completar cargue',
        href: '/paybridge/schedules',
        queryParams: { period: this.selectedPeriod(), sourceId: first.financialSourceId, action: 'upload' }
      });
    }
    if (this.failedReconciliationExecutions()) result.push({ tone: 'critical', title: `${this.failedReconciliationExecutions()} conciliaciones fallidas`, description: 'Revisa la configuración o el detalle de la última ejecución.', action: 'Abrir conciliación', href: '/reconciliation/source-agnostic/plans' });
    const fiscal = this.fiscalState();
    if (fiscal.failedRuns) result.push({ tone: 'critical', title: `${fiscal.failedRuns} ${fiscal.failedRuns === 1 ? 'conciliación fiscal fallida' : 'conciliaciones fiscales fallidas'}`, description: 'La última ejecución fiscal del periodo no terminó. Revisa el detalle y vuelve a ejecutarla.', action: 'Abrir conciliación fiscal', href: '/reconciliation/fiscal' });
    if (fiscal.notConciliated) result.push({ tone: 'warning', title: `${this.formatNumber(fiscal.notConciliated)} ${fiscal.notConciliated === 1 ? 'documento fiscal' : 'documentos fiscales'} con diferencia`, description: 'Documentos que no cruzaron entre la DIAN y el sistema financiero en la última ejecución.', action: 'Revisar diferencias', href: '/reconciliation/fiscal/diferencias' });
    if (this.canceledReconciliationExecutions()) result.push({ tone: 'warning', title: `${this.canceledReconciliationExecutions()} conciliaciones canceladas`, description: 'La última ejecución no tiene resultados consolidados para mostrar en el tablero.', action: 'Abrir conciliación', href: '/reconciliation/source-agnostic/plans' });
    // Si el tenant ya concilia en modo Fiscal no se le sugiere configurar fuentes financieras ni recorridos.
    if (!fiscal.hasRuns && !this.partialData() && !this.sources().length) result.push({ tone: 'info', title: 'Configura la primera fuente financiera', description: 'Conecta una cuenta, adquirente, agregador o POS para activar el pulso financiero.', action: 'Configurar fuentes', href: '/maestros/fuentes-bancarias' });
    else if (!fiscal.hasRuns && !this.partialData() && !this.routeCount()) result.push({ tone: 'info', title: 'Falta definir cómo se conciliará la operación', description: 'Define en Configuración los recorridos del dinero (por ejemplo, ventas → banco).', action: 'Ir a Configuración', href: '/reconciliation/configuracion' });
    return result.slice(0, 4);
  });

  ngOnInit(): void { this.loadInitialPeriod(); }
  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }
  selectPeriod(period: string): void {
    if (!period) return;
    this.periodMode.set('month'); this.periodFromDate.set(null); this.periodToDate.set(null);
    this.selectedPeriod.set(period); this.loadDashboard();
  }
  handlePeriodFilterApply(event: PeriodFilterApply): void {
    if (event.mode === 'range' && event.fromDate && event.toDate) {
      if (this.monthsBetween(event.fromDate, event.toDate) > 3) {
        this.error.set('Selecciona un rango de máximo tres meses para mantener una lectura clara y rápida.');
        return;
      }
      this.periodMode.set('range'); this.periodFromDate.set(event.fromDate); this.periodToDate.set(event.toDate);
    } else {
      this.periodMode.set('month'); this.periodFromDate.set(null); this.periodToDate.set(null);
    }
    this.selectedPeriod.set(event.period); this.loadDashboard();
  }
  refresh(): void { this.refreshing.set(true); this.loadDashboard(); }
  periodLabel(period = this.selectedPeriod()): string { return getPeriodLabelForApiPeriod(period); }
  dashboardPeriodLabel(): string {
    const dates = this.selectionDates();
    if (this.periodMode() !== 'range') return this.periodLabel();
    const formatter = new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', year: 'numeric' });
    return `${formatter.format(new Date(`${dates.fromDate}T00:00:00`))} – ${formatter.format(new Date(`${dates.toDate}T00:00:00`))}`;
  }
  formatMoney(value: number, compact = false): string { return new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', notation: compact ? 'compact' : 'standard', maximumFractionDigits: compact ? 1 : 0 }).format(value || 0); }
  /** Porcentaje con formato colombiano (coma decimal). */
  formatPercent(value: number): string { return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format(value || 0); }
  formatNumber(value: number): string { return new Intl.NumberFormat('es-CO').format(value || 0); }
  formatReconciliationShare(value: number): string {
    const total = this.totalReconciliationMovements();
    return total ? `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 1 }).format((value / total) * 100)}%` : '0%';
  }
  operationPerformancePercent(activity: FinancialSourceActivity | null): number {
    const credits = activity?.totalCredits ?? 0;
    if (!credits) return 0;
    return Math.round((((activity?.totalCredits ?? 0) - (activity?.totalDebits ?? 0)) / credits) * 1000) / 10;
  }
  flowHeight(amount: number, maximum: number): number { return Math.max(7, Math.round(Math.abs(amount) / maximum * 100)); }
  deliveryRate(): number { const stats = this.deliveryStats(); return stats.total ? Math.round(stats.received / stats.total * 100) : 0; }

  private loadInitialPeriod(): void {
    this.reconciliation.getActivityPeriods(FEED_TYPES).pipe(timeout(15_000), catchError(() => of([] as FinancialActivityPeriod[])), takeUntil(this.destroy$)).subscribe(periods => {
      const ordered = [...periods].sort((a, b) => b.period.localeCompare(a.period));
      this.periods.set(ordered); this.selectedPeriod.set(ordered[0]?.period ?? formatPeriodForApi(DEFAULT_PERIOD)); this.loadDashboard();
    });
  }
  private loadDashboard(): void {
    const { fromDate, toDate } = this.selectionDates();
    const selectedPeriods = this.periodsInRange(fromDate, toDate); this.error.set(null);
    this.partialData.set(false);
    const recover = <T>(request: Observable<T>, fallback: T, timeoutMs = 20_000): Observable<T> => request.pipe(
      timeout(timeoutMs),
      catchError(() => { this.partialData.set(true); return of(fallback); })
    );
    forkJoin({
      activity: recover(this.loadActivityRange(selectedPeriods, BANK_FEED_TYPES, fromDate, toDate), null),
      acquiringActivity: recover(this.loadActivityRange(selectedPeriods, ACQUIRING_FEED_TYPES, fromDate, toDate), null),
      aggregatorActivity: recover(this.loadActivityRange(selectedPeriods, AGGREGATOR_FEED_TYPES, fromDate, toDate), null),
      posActivity: recover(this.loadActivityRange(selectedPeriods, POS_FEED_TYPES, fromDate, toDate), null),
      readiness: recover(this.loadReadinessRange(selectedPeriods), null),
      sources: recover(this.reconciliation.getSources(), [] as FinancialSource[]),
      profiles: recover(this.reconciliation.getProfiles(), [] as FinancialProfile[]),
      sourceAgnosticState: recover(this.loadSourceAgnosticDashboardState(fromDate, toDate), this.emptySourceAgnosticDashboardState(), 45_000),
      deliveries: recover(this.schedules.getDeliveries(fromDate, toDate), [] as FinancialDeliveryOccurrence[]),
      fiscalState: recover(this.loadFiscalDashboardState(fromDate, toDate), EMPTY_FISCAL_DASHBOARD_STATE),
      integrations: this.integrationsApi.health().pipe(timeout(15_000), catchError(() => of([] as IntegrationJobHealth[])))
    }).pipe(takeUntil(this.destroy$)).subscribe({ next: result => {
      this.activity.set(result.activity); this.acquiringActivity.set(result.acquiringActivity); this.aggregatorActivity.set(result.aggregatorActivity); this.posActivity.set(result.posActivity); this.readiness.set(result.readiness); this.sources.set(result.sources.filter(source => source.isEnabled));
      this.profiles.set(result.profiles.filter(profile => profile.isEnabled)); this.fiscalState.set(result.fiscalState); this.failedIntegrations.set(result.integrations.filter(job => job.health === 'Failed')); this.reconciliationStatus.set(this.mergeFiscalStatus(result.sourceAgnosticState.status, result.fiscalState)); this.failedReconciliationExecutions.set(result.sourceAgnosticState.failedExecutions); this.canceledReconciliationExecutions.set(result.sourceAgnosticState.canceledExecutions); this.routeCount.set(result.sourceAgnosticState.routeCount ?? 0); this.deliveries.set(result.deliveries);
      this.updatedAt.set(new Date()); this.loading.set(false); this.refreshing.set(false);
    }, error: () => { this.error.set('No fue posible actualizar el pulso financiero. Inténtalo nuevamente.'); this.loading.set(false); this.refreshing.set(false); } });
  }
  private loadSourceAgnosticDashboardState(periodFrom: string, periodTo: string): Observable<SourceAgnosticDashboardState> {
    return this.sourceAgnostic.workbench(periodFrom, periodTo, 1, 25).pipe(
      switchMap(workbench => {
        const completedItems = workbench.items.filter(item => item.latestStatus?.toLowerCase() === 'completed');
        const executionIds = [...new Set(completedItems.map(item => item.latestExecutionId).filter((id): id is string => !!id))];
        const failedExecutions = workbench.items.filter(item => item.latestStatus?.toLowerCase() === 'failed').length;
        const canceledExecutions = workbench.items.filter(item => item.latestStatus?.toLowerCase() === 'canceled').length;
        const routeCount = workbench.items.length;
        if (!executionIds.length) return of({ status: this.emptyReconciliationStatus(), failedExecutions, canceledExecutions, routeCount });

        return forkJoin(executionIds.map(id => this.loadExecutionStatusCounts(id))).pipe(
          map(statuses => ({
            status: statuses.reduce((acc, item) => ({
              reconciled: acc.reconciled + item.reconciled,
              differences: acc.differences + item.differences,
              pending: acc.pending + item.pending,
              total: acc.total + item.total
            }), this.emptyReconciliationStatus()),
            failedExecutions,
            canceledExecutions,
            routeCount
          }))
        );
      })
    );
  }
  /**
   * Conteos en la misma unidad (saldos de movimiento). En el motor, 'pending' incluye los vencidos
   * ('review' ⊂ 'pending') e 'in-time' los que aún están en plazo, así que:
   * sin conciliar = pending + in-time; con diferencias = review; pendientes = sin conciliar − review.
   */
  private loadExecutionStatusCounts(executionId: string): Observable<ReconciliationStatusSummary> {
    return forkJoin({
      total: this.countBalancesByView(executionId, ''),
      review: this.countBalancesByView(executionId, 'review'),
      pending: this.countBalancesByView(executionId, 'pending'),
      inTime: this.countBalancesByView(executionId, 'in-time')
    }).pipe(map(counts => {
      const unreconciled = counts.pending + counts.inTime;
      const total = Math.max(counts.total, unreconciled);
      return {
        reconciled: total - unreconciled,
        differences: counts.review,
        pending: Math.max(0, unreconciled - counts.review),
        total
      };
    }));
  }
  private countBalancesByView(executionId: string, view: '' | 'pending' | 'in-time' | 'review'): Observable<number> {
    return forkJoin({
      left: this.sourceAgnostic.balances(executionId, 1, 1, '', '', 'Left', '', view, 'amount', 'desc').pipe(map(page => page.total), catchError(() => of(0))),
      right: this.sourceAgnostic.balances(executionId, 1, 1, '', '', 'Right', '', view, 'amount', 'desc').pipe(map(page => page.total), catchError(() => of(0)))
    }).pipe(map(result => result.left + result.right));
  }
  private loadFiscalDashboardState(fromDate: string, toDate: string): Observable<FiscalDashboardState> {
    return loadFiscalDashboardState(this.fiscalApi, fromDate, toDate);
  }
  /** Fiscal suma documentos conciliados y con diferencia; no tiene "pendientes en plazo". */
  private mergeFiscalStatus(status: ReconciliationStatusSummary, fiscal: FiscalDashboardState): ReconciliationStatusSummary {
    if (!fiscal.hasRuns) return status;
    return {
      reconciled: status.reconciled + fiscal.conciliated,
      differences: status.differences + fiscal.notConciliated,
      pending: status.pending,
      total: status.total + fiscal.total
    };
  }
  private emptySourceAgnosticDashboardState(): SourceAgnosticDashboardState {
    return { status: this.emptyReconciliationStatus(), failedExecutions: 0, canceledExecutions: 0 };
  }
  private emptyReconciliationStatus(): ReconciliationStatusSummary {
    return { reconciled: 0, differences: 0, pending: 0, total: 0 };
  }
  chartPoints(kind: 'credits' | 'debits' | 'balance'): string {
    const days = this.activity()?.dailyBreakdown ?? [];
    if (!days.length) return '';
    const values = days.map(item => kind === 'credits'
      ? item.totalCredits
      : kind === 'debits'
        ? item.totalDebits
        : Math.max(0, item.totalCredits - item.totalDebits));
    const max = Math.max(1, ...days.flatMap(item => [item.totalCredits, item.totalDebits, Math.max(0, item.totalCredits - item.totalDebits)]));
    const width = 640;
    const height = 176;
    return values.map((value, index) => {
      const x = values.length === 1 ? width / 2 : index * (width / (values.length - 1));
      const y = height - (value / max) * (height - 12) + 6;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }
  chartLabel(position: 'first' | 'middle' | 'last'): string {
    const days = this.activity()?.dailyBreakdown ?? [];
    if (!days.length) return 'Sin datos';
    const index = position === 'first' ? 0 : position === 'last' ? days.length - 1 : Math.floor((days.length - 1) / 2);
    return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short' }).format(new Date(`${days[index].date}T00:00:00`));
  }
  chartTickLabels(): string[] {
    const days = this.activity()?.dailyBreakdown ?? [];
    if (!days.length) return [];
    if (days.length <= 8) return days.map(day => new Date(`${day.date}T00:00:00`).getDate().toString());
    return days
      .filter((_, index) => index % 2 === 0 || index === days.length - 1)
      .map(day => new Date(`${day.date}T00:00:00`).getDate().toString());
  }
  private loadActivityRange(periods: string[], feedTypes: string[], fromDate: string, toDate: string): Observable<FinancialSourceActivity> {
    return forkJoin(periods.map(period => this.reconciliation.getActivity(period, feedTypes, {
      page: 1, pageSize: 1, fromDate, toDate
    }))).pipe(map(results => this.mergeActivities(results)));
  }
  private mergeActivities(results: FinancialSourceActivity[]): FinancialSourceActivity {
    const base = results[0];
    if (!base) throw new Error('No hay periodos para consultar.');
    const sum = (key: keyof FinancialSourceActivity) => results.reduce((total, item) => total + Number(item[key] ?? 0), 0);
    const mergeBreakdown = (key: 'franchiseBreakdown' | 'categoryBreakdown') => {
      const merged = new Map<string, FinancialSourceActivity[typeof key][number]>();
      for (const result of results) for (const item of result[key]) {
        const current = merged.get(item.key);
        merged.set(item.key, current ? {
          ...current,
          recordCount: current.recordCount + item.recordCount,
          totalAmount: current.totalAmount + item.totalAmount,
          totalGrossAmount: current.totalGrossAmount + item.totalGrossAmount,
          totalNetAmount: current.totalNetAmount + item.totalNetAmount,
          totalCredits: current.totalCredits + item.totalCredits,
          totalDebits: current.totalDebits + item.totalDebits
        } : { ...item });
      }
      return [...merged.values()];
    };
    const dailyBreakdown = results.flatMap(item => item.dailyBreakdown).sort((a, b) => a.date.localeCompare(b.date));
    const sources = new Map<string, FinancialSourceActivity['sources'][number]>();
    for (const result of results) for (const source of result.sources) {
      const current = sources.get(source.sourceId);
      sources.set(source.sourceId, current ? {
        ...current,
        recordCount: current.recordCount + source.recordCount,
        totalAmount: current.totalAmount + source.totalAmount,
        totalGrossAmount: current.totalGrossAmount + source.totalGrossAmount,
        totalNetAmount: current.totalNetAmount + source.totalNetAmount,
        lastLoadedAt: [current.lastLoadedAt, source.lastLoadedAt].filter(Boolean).sort().at(-1) ?? null
      } : { ...source });
    }
    return {
      ...base,
      period: periodsLabel(results), page: 1, totalPages: 1,
      totalItems: sum('totalItems'), totalAmount: sum('totalAmount'), totalCredits: sum('totalCredits'),
      totalDebits: sum('totalDebits'), totalGrossAmount: sum('totalGrossAmount'), totalNetAmount: sum('totalNetAmount'),
      totalFeeAmount: sum('totalFeeAmount'), totalTaxAmount: sum('totalTaxAmount'), totalRetentionAmount: sum('totalRetentionAmount'),
      totalInterestAmount: sum('totalInterestAmount'), totalVatAmount: sum('totalVatAmount'), totalGmfAmount: sum('totalGmfAmount'),
      excludedItems: sum('excludedItems'), openingBalance: results[0].openingBalance, closingBalance: results.at(-1)?.closingBalance,
      franchiseBreakdown: mergeBreakdown('franchiseBreakdown'), categoryBreakdown: mergeBreakdown('categoryBreakdown'),
      dailyBreakdown, sources: [...sources.values()], items: []
    };
  }
  private loadReadinessRange(periods: string[]): Observable<FinancialPeriodReadiness> {
    return forkJoin(periods.map(period => this.reconciliation.getReadiness(period))).pipe(map(results => {
      const sources = new Map<string, FinancialPeriodReadiness['sources'][number]>();
      for (const result of results) for (const source of result.sources) {
        const current = sources.get(source.sourceId);
        sources.set(source.sourceId, current ? {
          ...current,
          isReady: current.isReady || source.isReady,
          acceptedRows: current.acceptedRows + source.acceptedRows,
          lastLoadedAt: [current.lastLoadedAt, source.lastLoadedAt].filter(Boolean).sort().at(-1) ?? null
        } : { ...source });
      }
      const values = [...sources.values()];
      return {
        period: periods.join(','), sources: values,
        requiredSources: values.filter(source => source.isRequired).length,
        readySources: values.filter(source => source.isReady).length,
        isReady: values.length > 0 && values.every(source => source.isReady)
      };
    }));
  }
  chartDayLabel(date: string): string {
    return new Intl.DateTimeFormat('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(`${date}T00:00:00`));
  }
  chartPointPercent(index: number): number {
    const count = this.activity()?.dailyBreakdown?.length ?? 0;
    return count <= 1 ? 50 : index * (100 / (count - 1));
  }
  chartPointYPercent(index: number): number {
    const days = this.activity()?.dailyBreakdown ?? [];
    const day = days[index];
    if (!day) return 50;
    const max = Math.max(1, ...days.flatMap(item => [item.totalCredits, item.totalDebits, Math.max(0, item.totalCredits - item.totalDebits)]));
    const y = 176 - (Math.max(0, day.totalCredits - day.totalDebits) / max) * 164 + 6;
    return y / 190 * 100;
  }
  chartBalance(credits: number, debits: number): number { return credits - debits; }
  setActiveFlowDay(index: number | null): void { this.activeFlowIndex.set(index); }
  private periodDates(period: string): { fromDate: string; toDate: string } {
    const year = Number(period.slice(0, 4)); const month = Number(period.slice(4, 6)); const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate();
    return { fromDate: `${year}-${String(month).padStart(2, '0')}-01`, toDate: `${year}-${String(month).padStart(2, '0')}-${lastDay}` };
  }
  private selectionDates(): { fromDate: string; toDate: string } {
    return this.periodMode() === 'range' && this.periodFromDate() && this.periodToDate()
      ? { fromDate: this.periodFromDate()!, toDate: this.periodToDate()! }
      : this.periodDates(this.selectedPeriod());
  }
  private periodsInRange(fromDate: string, toDate: string): string[] {
    const [fromYear, fromMonth] = fromDate.split('-').map(Number);
    const [toYear, toMonth] = toDate.split('-').map(Number);
    const result: string[] = [];
    for (let date = new Date(Date.UTC(fromYear, fromMonth - 1, 1)); date <= new Date(Date.UTC(toYear, toMonth - 1, 1)); date.setUTCMonth(date.getUTCMonth() + 1)) {
      result.push(`${date.getUTCFullYear()}${String(date.getUTCMonth() + 1).padStart(2, '0')}`);
    }
    return result;
  }
  private monthsBetween(fromDate: string, toDate: string): number { return this.periodsInRange(fromDate, toDate).length; }
}

function periodsLabel(results: FinancialSourceActivity[]): string {
  return results.map(result => result.period).join(',');
}
